<template>
  <div class="auto-payment-transactions">
    <div class="table-title">{{ st('finance.autoPaymentTransactionsTitle') }}</div>
    <artmisads-table
      :columns="columns"
      :data-source="dataList"
      :loading="loading"
      :row-key="record => record.id ?? record.paymentId ?? record.billingId"
      :change="onChangePage"
      :pagination="{
        total: pagination.total,
        current: pagination.page,
        pageSize: pagination.pageSize,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
      }"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'time'">
          {{ formatTime(record) }}
        </template>
        <template v-else-if="column.key === 'billingDate'">
          {{ formatBillingDate(record) }}
        </template>
        <template v-else-if="column.key === 'paymentId'">
          {{ record.paymentId || record.billingId || '--' }}
        </template>
        <template v-else-if="column.key === 'amount'">
          {{ formatAmount(record) }}
        </template>
        <template v-else-if="column.key === 'method'">
          <div class="method-list">
            <div>
              {{ record.methodType }} 
            </div>
           <div>
            {{ record.methodTail }}
           </div>
          </div>
        </template>
      </template>
    </artmisads-table>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import moment from 'moment'
import { financeApi } from '~/server'
import { useSellerI18n } from '@/composables/useSellerI18n'
import { CURRENCY_DATA_MAPPER, COUNTRY_TIME_TZ } from '@/contants/common'
import { useUserInfoStore } from "@/storeData"
const { st } = useSellerI18n()
const store = useUserInfoStore()
const userInfo = store.userInfo
const timeZone = COUNTRY_TIME_TZ[userInfo.countryCode] || 'US'
const loading = ref(false)
const dataList = ref([])
const pagination = ref({
  page: 1,
  pageSize: 10,
  total: 0,
})

const METHOD_LABEL_MAP = {
  CARD: 'CreditCard',
  CREDIT_CARD: 'CreditCard',
  ALIPAY_CN: 'Alipay',
  ALIPAY: 'Alipay',
}

const columns = computed(() => [
  {
    title: st('finance.colTime'),
    key: 'time',
    dataIndex: 'time',
    width: 180,
  },
  {
    title: st('finance.billingDate'),
    key: 'billingDate',
    dataIndex: 'billingDate',
    width: 120,
  },
  {
    title: st('finance.paymentId'),
    key: 'paymentId',
    dataIndex: 'paymentId',
    width: 260,
  },
  {
    title: st('finance.colAmount'),
    key: 'amount',
    dataIndex: 'amount',
    width: 120,
  },
  {
    title: st('finance.payContColMethod'),
    key: 'method',
    dataIndex: 'method',
    width: 180,
  },
])

const formatTime = record => {
  const time = record.transactionTime
  if (!time) return '--'
  return moment(Number(time)).tz(timeZone).format('YYYY-MM-DD HH:mm:ss')
}

const formatBillingDate = record => {
  const billingDate = record.billingDateTimestamp ?? record.billingDateStr ?? record.billingDateTimestamp
  if (!billingDate) return '--'
  if (/^\d{4}-\d{2}$/.test(String(billingDate))) {
    return billingDate
  }
  return moment(Number(billingDate)).tz(timeZone).format('YYYY-MM')
}

const formatAmount = record => {
  const amount = record.amount
  if (amount === undefined || amount === null || amount === '') return '--'
  const currency = CURRENCY_DATA_MAPPER[record.currency] || record.currencySymbol || '$'
  return `${currency} ${Number(amount).toFixed(2)}`
}

const formatInstrumentLabel = method => {
  if (!method) return ''
  if (typeof method === 'string') return method
  const kind = method.instrumentKind || method.kind || method.paymentMethod || ''
  const label = METHOD_LABEL_MAP[kind] || method.methodName || kind
  const suffix =
    method.maskedNo ||
    method.maskedCardNo ||
    method.cardNoMasked ||
    method.last4 ||
    method.cardLast4 ||
    ''
  const digits = String(suffix).replace(/\D/g, '').slice(-4)
  return digits ? `${label} ${digits}` : label
}

const formatMethods = record => {
  const raw =
    record.paymentMethods ??
    record.methods ??
    record.instruments ??
    record.paymentMethodList ??
    record.paymentMethod
  if (!raw) return ['--']
  if (Array.isArray(raw)) {
    const labels = raw.map(formatInstrumentLabel).filter(Boolean)
    return labels.length ? labels : ['--']
  }
  const label = formatInstrumentLabel(raw)
  return label ? [label] : ['--']
}

const getList = async () => {
  loading.value = true
  try {
    const response = await financeApi.financeSellerBankAutoPaymentTransactionList({
      page: pagination.value.page,
      pageSize: pagination.value.pageSize,
    })
    const list = response?.data?.data ?? response?.data ?? []
    dataList.value = Array.isArray(list) ? list : []
    pagination.value.total = response?.data?.total ?? dataList.value.length
  } finally {
    loading.value = false
  }
}

const onChangePage = ({ current, pageSize }) => {
  pagination.value.page = current
  pagination.value.pageSize = pageSize
  getList()
}

onMounted(() => {
  getList()
})
</script>

<style lang="less" scoped>
.auto-payment-transactions {
  margin-top: 16px;
  padding: 16px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 0 0 #0f06290d;

  .table-title {
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--border-4);
    font-family: Sora;
    font-weight: 600;
    font-size: 20px;
    line-height: 28px;
    letter-spacing: 0.5px;
    color: #0f0629;
  }

  .method-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    line-height: 20px;
  }
}
</style>
